import { useState, useCallback } from 'react';
import uniqBy from 'lodash/uniqBy';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useMigrationIdManager,
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { APP_IDENTIFIER } from '../../common/constants';
import { EXPERIENCES, startImportInteractionMetric } from '../../common/constants/metrics';
import type { Source } from '../../common/constants/source';
import type { AdvancedOptions, MappingState, MigrationId, ProjectData } from '../../common/types';
import { useAnalyticsAttributesContext } from '../../common/utils/analtics-attributes-context';
import { useGetCSVImportClient } from '../../common/utils/csv-import-clients';
import type { UseStartX2CImport } from './types';
import { transformToStartImportPayload } from './utils';

export const useStartX2CImport = (source: Source, serviceName?: string): UseStartX2CImport => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.START_IMPORT,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);

	const { storeMigrationId } = useMigrationIdManager();

	const analyticsAttributes = useAnalyticsAttributesContext();
	const client = useGetCSVImportClient(source, serviceName);

	const startX2CImport = useCallback(
		async (
			migrationId: MigrationId,
			mappingState: MappingState,
			projectData: ProjectData,
			advancedOptions: AdvancedOptions,
			onSuccess: () => void,
		) => {
			setIsLoading(true);
			setHasErrorOccurred(false);
			startImportInteractionMetric.start();
			onExperienceStart();

			try {
				const payload = transformToStartImportPayload(
					mappingState,
					projectData,
					advancedOptions,
					ff('x2c-issuelinking-frontend_tuyzk'),
				);

				fireTrackAnalytics(createAnalyticsEvent({}), 'import started', {
					numberOfColumnsInCSV: Object.keys(mappingState.columns).length,
					numberOfFieldsMappedAutomatically: mappingState.preMappedFieldCount,
					numberOfFieldsExcluded: Object.keys(mappingState.columns).filter(
						(name) => !mappingState.columns[name].isIncluded,
					).length,
					numberOfColumnsToImport: payload.issueLinkColumns
						? payload.columns.length + payload.issueLinkColumns.length
						: payload.columns.length,
					numberOfNewFieldsToCreate: payload.columns.filter(
						(c) => !!c.field && c.field.fieldId === null,
					).length,
					numIssueLinksMapped: payload.issueLinkColumns ? payload.issueLinkColumns.length : 0,
					numJiraFieldsMapped: payload.columns.length,
					fileEncoding: payload.fileEncoding,
					delimiter: payload.delimiter,
					projectStyle: payload.projectData.style,
					accessLevel: payload.projectData.accessLevel,
					template: payload.projectData.template,
					shouldImportUsersToggle: mappingState.shouldImportUsers,
					numberOfPrefilledUsers: mappingState.preFilledUsersCount,
					usersMapped: payload.userMapping ? payload.userMapping.length : 0,
					numberOfUniqueUsers:
						mappingState.shouldImportUsers && payload.userMapping
							? uniqBy(Object.values(payload.userMapping), 'userValue').filter(
									(user) => user.userValue,
								).length
							: 0,
					...payload.dateProperties,
				});

				await client.startImport({
					migrationId,
					mappingState,
					projectData,
					advancedOptions,
				});
				setIsLoading(false);

				storeMigrationId(projectData.key, migrationId);

				startImportInteractionMetric.stop();
				onExperienceSuccess({ migrationId, ...analyticsAttributes });

				onSuccess();
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				startImportInteractionMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, {
					migrationId,
					...analyticsAttributes,
				});
				fireErrorAnalytics({
					meta: {
						id: 'start-x2c-import-error',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
					error,
				});

				setHasErrorOccurred(true);
				setIsLoading(false);
			}
		},
		[
			onExperienceStart,
			createAnalyticsEvent,
			storeMigrationId,
			onExperienceSuccess,
			analyticsAttributes,
			client,
			onExperienceFail,
		],
	);

	return { isLoading, hasErrorOccurred, startX2CImport };
};
